import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe, ChevronDown, Check } from 'lucide-react';

/* 
  NOTE: This component uses the "Cookie Method" to control Google Translate.
  Instead of using the ugly Google widget directly, we:
  1. Hide the Google widget with CSS.
  2. When a user selects a language, we set the 'googtrans' cookie.
  3. We reload the page to apply the translation.
  
  This ensures the UI remains beautiful and custom while using Google's powerful engine.
*/

const regionalLanguages = [
    { code: 'hi', name: 'Hindi', native: 'हिन्दी' },
    { code: 'bn', name: 'Bengali', native: 'বাংলা' },
    { code: 'te', name: 'Telugu', native: 'తెలుగు' },
    { code: 'mr', name: 'Marathi', native: 'मराठी' },
    { code: 'ta', name: 'Tamil', native: 'தமிழ்' },
    { code: 'gu', name: 'Gujarati', native: 'ગુજરાતી' },
    { code: 'kn', name: 'Kannada', native: 'ಕನ್ನಡ' },
    { code: 'ml', name: 'Malayalam', native: 'മലയാളം' },
    { code: 'pa', name: 'Punjabi', native: 'ਪੰਜਾਬੀ' },
    { code: 'ur', name: 'Urdu', native: 'اردو' },
    { code: 'or', name: 'Odia', native: 'ଓଡ଼ିଆ' },
    { code: 'as', name: 'Assamese', native: 'অসমীয়া' },
    { code: 'ne', name: 'Nepali', native: 'नेपाली' },
    { code: 'sd', name: 'Sindhi', native: 'سنڌي' },
    { code: 'sa', name: 'Sanskrit', native: 'संस्कृतम्' },
];

const foreignLanguages = [
    { code: 'en', name: 'English', native: 'English' },
    { code: 'es', name: 'Spanish', native: 'Español' },
    { code: 'es-MX', name: 'Spanish (Mexico)', native: 'Español (MX)' },
    { code: 'fr', name: 'French', native: 'Français' },
    { code: 'pt', name: 'Portuguese', native: 'Portugese' },
    { code: 'de', name: 'German', native: 'Deutsch' },
    { code: 'it', name: 'Italian', native: 'Italiano' },
    { code: 'nl', name: 'Dutch', native: 'Nederlands' },
    { code: 'da', name: 'Danish', native: 'Dansk' },
    { code: 'sv', name: 'Swedish', native: 'Svenska' },
    { code: 'pl', name: 'Polish', native: 'Polski' },
    { code: 'uk', name: 'Ukrainian', native: 'Українська' },
    { code: 'el', name: 'Greek', native: 'Ελληνικά' },
    { code: 'ro', name: 'Romanian', native: 'Română' },
    { code: 'ru', name: 'Russian', native: 'Русский' },
    { code: 'tr', name: 'Turkish', native: 'Türkçe' },
    { code: 'zh-CN', name: 'Chinese', native: '简体中文' },
    { code: 'zh-TW', name: 'Chinese (Taiwan)', native: '繁體中文' },
    { code: 'ja', name: 'Japanese', native: '日本語' },
    { code: 'ko', name: 'Korean', native: '한국어' },
    { code: 'ar', name: 'Arabic', native: 'العربية' },
    { code: 'he', name: 'Hebrew', native: 'עברית' },
    { code: 'ms', name: 'Malay', native: 'Bahasa Melayu' },
    { code: 'id', name: 'Indonesian', native: 'Bahasa Indonesia' },
    { code: 'th', name: 'Thai', native: 'ไทย' },
    { code: 'vi', name: 'Vietnamese', native: 'Tiếng Việt' },
    { code: 'si', name: 'Sinhalese', native: 'සිංහල' },
    { code: 'ga', name: 'Irish', native: 'Gaeilge' },
    { code: 'gd', name: 'Scottish Gaelic', native: 'Gàidhlig' },
];

const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
};

const LanguageSelector = () => {
    // Determine current language from cookie or default to 'en'
    const [currentLang, setCurrentLang] = useState('en');

    useEffect(() => {
        // Initialize Google Translate Script
        if (!document.getElementById('google_translate_script')) {
            const script = document.createElement('script');
            script.id = 'google_translate_script';
            script.type = 'text/javascript';
            script.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
            script.async = true;
            document.body.appendChild(script);

            window.googleTranslateElementInit = () => {
                new window.google.translate.TranslateElement(
                    {
                        pageLanguage: 'en',
                        autoDisplay: false,
                        includedLanguages: 'en,hi,bn,te,mr,ta,gu,kn,ml,pa,ur,or,as,ne,sd,si,sa,es,fr,pt,de,it,nl,da,sv,pl,uk,el,ro,ru,tr,zh-CN,zh-TW,ja,ko,ar,he,ms,id,th,vi,ga,gd',
                    },
                    'google_translate_element'
                );
            };
        }

        // Check for existing cookie
        const cookie = getCookie('googtrans');
        if (cookie) {
            // Cookie format is usually /en/targetLang or /auto/targetLang
            const lang = cookie.split('/').pop();
            if (lang) setCurrentLang(lang);
        }
    }, []);

    const handleLanguageChange = (langCode) => {
        // Set the google translate cookie
        document.cookie = `googtrans=/en/${langCode}; path=/; domain=${window.location.hostname}`;
        document.cookie = `googtrans=/en/${langCode}; path=/;`; // Fallback for localhost

        setCurrentLang(langCode);
        window.location.reload(); // Reload to apply translation cleanly
    };

    return (
        <div className="flex items-center gap-2">
            {/* Hidden Google Element */}
            <div id="google_translate_element" className="hidden absolute opacity-0 pointer-events-none h-0 w-0 overflow-hidden"></div>

            {/* Custom UI */}
            <Dropdown
                label="Regional"
                langs={regionalLanguages}
                currentLang={currentLang}
                onSelect={handleLanguageChange}
                icon={Globe}
            />
            <Dropdown
                label="Foreign"
                langs={foreignLanguages}
                currentLang={currentLang}
                onSelect={handleLanguageChange}
                icon={Globe}
            />
        </div>
    );
};

const Dropdown = ({ label, langs, currentLang, onSelect, icon: Icon }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div className="relative">
            <button
                onClick={() => setIsOpen(!isOpen)}
                aria-label={`Select ${label} language`}
                aria-expanded={isOpen}
                aria-haspopup="listbox"
                className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-dark-card/40 border border-white/10 hover:border-primary/50 transition-all duration-300 text-sm font-medium whitespace-nowrap"
            >
                <Icon size={16} className="text-primary" />
                <span className="hidden sm:inline">{label}</span>
                <ChevronDown size={14} className={`transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            <AnimatePresence>
                {isOpen && (
                    <>
                        <div
                            className="fixed inset-0 z-40"
                            onClick={() => setIsOpen(false)}
                        />
                        <motion.div
                            initial={{ opacity: 0, y: 10, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 10, scale: 0.95 }}
                            role="listbox"
                            transition={{ type: 'spring', damping: 20, stiffness: 300 }}
                            className="absolute right-0 mt-2 w-48 rounded-2xl bg-dark-card border border-white/10 shadow-2xl overflow-hidden z-50 max-h-[400px] flex flex-col"
                        >
                            <div className="py-2 overflow-y-auto custom-scrollbar flex-1">
                                {langs.map((lang) => (
                                    <button
                                        key={lang.code}
                                        onClick={() => {
                                            onSelect(lang.code);
                                            setIsOpen(false);
                                        }}
                                        role="option"
                                        aria-selected={currentLang === lang.code}
                                        aria-label={`Change language to ${lang.name}`}
                                        className={`w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors hover:bg-primary/10 ${currentLang === lang.code ? 'text-primary bg-primary/5' : 'text-light-muted hover:text-light'
                                            }`}
                                    >
                                        <div className="flex flex-col items-start notranslate">
                                            <span className="font-bold text-sm">{lang.name}</span>
                                            <span className="text-[11px] text-light-muted font-normal">{lang.native}</span>
                                        </div>
                                        {currentLang === lang.code && <Check size={14} />}
                                    </button>
                                ))}
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </div>
    );
};

export default LanguageSelector;
